/** How often an open page asks whether a newer version of the site exists. */
export const UPDATE_CHECK_MS = 15 * 60 * 1000;

/**
 * Keeps an open page from staying on an old copy for days. It asks for a newer version every 15
 * minutes and whenever the tab comes back into view, and calls `onTakeover` once a newer version
 * of the background worker has taken over this page. Returns a function that stops all of it.
 */
export function watchForNewVersion(
  getRegistration: () => ServiceWorkerRegistration | undefined,
  onTakeover: () => void,
): () => void {
  const container = typeof navigator === "undefined" ? undefined : navigator.serviceWorker;
  const check = () => {
    void getRegistration()
      ?.update()
      .catch(() => undefined);
  };
  const timer = window.setInterval(check, UPDATE_CHECK_MS);
  const onShow = () => {
    if (document.visibilityState === "visible") check();
  };
  document.addEventListener("visibilitychange", onShow);
  // On a first visit nothing controls the page yet, so the worker claiming it is not an update.
  const wasControlled = Boolean(container?.controller);
  const onChange = () => {
    if (wasControlled) onTakeover();
  };
  container?.addEventListener("controllerchange", onChange);
  return () => {
    window.clearInterval(timer);
    document.removeEventListener("visibilitychange", onShow);
    container?.removeEventListener("controllerchange", onChange);
  };
}
